import { format } from "date-fns"
import ExcelJS from "exceljs"

/**
 * @typedef {Object} ExcelUtils
 * @property {Method} export - Export to excel from column (Array of String) and values (Object)
 * @property {Method} import - Import from excel returning Array of Sheet, with Sheet being Array of Object
 */
class ExcelUtils {
    static export(export_name: string, column: string[], values: { [key: string]: string }[]) {
        // Create a new workbook
        const workbook = new ExcelJS.Workbook()
        const worksheet = workbook.addWorksheet(export_name)

        const numberToLetter = (number: number) => {
            const letters = [
                "A", "B", "C", "D", "E", "F", "G", "H",
                "I", "J", "K", "L", "M", "N", "O", "P",
                "Q", "R", "S", "T", "U", "V", "W", "X",
                "Y", "Z",
                "AA", "AB", "AC", "AD", "AE", "AF", "AG", "AH",
                "AI", "AJ", "AK", "AL", "AM", "AN", "AO", "AP",
                "AQ", "AR", "AS", "AT", "AU", "AV", "AW", "AX",
                "AY", "AZ"
            ]

            if (number >= 0 && number <= 51) {
                return letters[number]
            }

            return "Invalid number"
        }

        const startRow = 2
        column.map((key, index) => {
            worksheet.getColumn(`${numberToLetter(index)}`).width = 20
            worksheet.getCell(`${numberToLetter(index)}${startRow - 1}`).value = String(key.toUpperCase())
            worksheet.getCell(`${numberToLetter(index)}${startRow - 1}`).style = {
                fill: {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "c8c8c8" }, // Yellow background
                },
                font: {
                    bold: true, // Bold font style
                },
                alignment: {
                    vertical: "middle", // Align vertically at the middle
                    horizontal: "center",
                },
                border: {
                    top: {
                        style: "thin",
                        color: { argb: "000000" },
                    },
                    left: {
                        style: "thin",
                        color: { argb: "000000" },
                    },
                    right: {
                        style: "thin",
                        color: { argb: "000000" },
                    },
                    bottom: {
                        style: "thin",
                        color: { argb: "000000" },
                    },
                },
            }
        })

        // Add table data
        values.map((val, index) => {
            const row: string[] = []
            column.map((key) => {
                if (val[key] != null) {
                    row.push(val[key])
                } else {
                    row.push('')
                }
            })
            const dataRow = worksheet.addRow(row)
            dataRow.eachCell((cell) => {
                cell.style = {
                    alignment: {
                        vertical: "middle",
                        horizontal: "center",
                    },
                    border: {
                        top: {
                            style: "thin",
                            color: { argb: "000000" },
                        },
                        left: {
                            style: "thin",
                            color: { argb: "000000" },
                        },
                        right: {
                            style: "thin",
                            color: { argb: "000000" },
                        },
                        bottom: {
                            style: "thin",
                            color: { argb: "000000" },
                        },
                    },
                }
            })
        })

        // Generate the Excel file
        workbook.xlsx.writeBuffer().then((buffer) => {
            // Create a Blob from the buffer
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            })

            // Create a download link
            const link = document.createElement("a")
            link.href = window.URL.createObjectURL(blob)
            link.download = `${export_name}.xlsx`

            // Simulate a click on the link to trigger the download
            link.click()

            // Clean up the URL object
            window.URL.revokeObjectURL(link.href)
        })
    }

    static async import(data: ArrayBuffer): Promise<{ [key: string]: string }[]> {
        // Create a new workbook
        const workbook = new ExcelJS.Workbook()
        await workbook.xlsx.load(data)
        const book: { [key: string | number]: any }[][] = []
        workbook.eachSheet((worksheet) => {
            const sheet: { [key: string | number]: any }[] = []
            worksheet.eachRow((row) => {
                sheet.push(row.values)
            })
            book.push(sheet)
        })
        // we only read sheet number 1
        if (book.length > 0) {
            const sheet = book[0]
            const result: { [key: string]: string }[] = []
            for (let i = 1; i < sheet.length; i++) {
                const obj: { [key: string]: string } = {}
                for (let j = 0; j < sheet[i].length; j++) {
                    if (sheet[0][j] == null || typeof (sheet[0][j]) != 'string') continue
                    if (sheet[i][j] instanceof Date) {
                        obj[sheet[0][j].toLowerCase()] = format(sheet[i][j], "yyyy-MM-dd HH:mm:ss")
                    } else {
                        obj[sheet[0][j].toLowerCase()] = typeof (sheet[i][j]) == 'object' ? sheet[i][j].result : sheet[i][j] ?? ''
                    }
                }
                result.push(obj)
            }
            return result
        } else {
            return []
        }
    }
}

export default ExcelUtils